import { FC, useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useGetCountryListForDropdowns,
  useGetTimeZoneList,
} from "../../../../api/services/client/clientApis";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTextField,
} from "../../../../components";
import {
  CountryField,
  CountryFieldDetails,
  TextFieldWithValidation,
} from "../../../../components/textFieldWithValidation/TextFieldWithValidation";
import {
  MAX_LENGTH_10,
  MAX_LENGTH_50,
  MAX_LENGTH_75,
  PAGE_IDS,
} from "../../../../helpers/constants";
import {
  getVatPostalDetailsFromCountryKey,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";

import { useGetBusinessUnitsByCountryQuery } from "../../../../api/services/mdmOffice/mdmOfficeApis";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { userPreferredLanguageSelector } from "../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { MdmCreateOfficeFormKeys } from "../../modelsAndSchemas/models";
import { mdmCreateOfficeSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";

const pageId = PAGE_IDS.MDM_ADDRESS_AND_CONTACT_DETAILS;

interface AddressAndContactDetailsProps {
  postalCodeRef: React.RefObject<{
    setDefaultValue: (values?: string) => void;
  }>;
  isEdit?: boolean;
  countryVatPostalData: CountryField[];
}

export const AddressAndContactDetails: FC<AddressAndContactDetailsProps> = ({
  postalCodeRef,
  isEdit = false,
  countryVatPostalData,
}) => {
  const {
    useFetchLabel,
    commonLabels,
    commonValidationMessages,
    fetchValidationMessage,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const {
    control,
    getValues,
    resetField,
    formState: { errors },
  } = useFormContext();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const { data: countryListData } = useGetCountryListForDropdowns();
  const { loggedInUser } = useGetLoggedInUserDetails();

  const {
    createNewOfficeTitle,
    address1,
    address2,
    address3,
    addressCountry,
    city,
    stateOrProvince,
    postalCode: postalCodeKey,
    phoneNumber: phoneNumberKey,
    emailAddress,
    timeZone,
    businessUnit,
  } = mdmCreateOfficeSiteCoreKeys;

  const getLabel = useFetchLabel(createNewOfficeTitle);

  const { phoneNumber, phoneISOCode } = getValues();
  const [isDefaultPostalCodeSet, setIsDefaultPostalCodeSet] = useState(false);

  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const watchPostalCode = useWatch({
    name: MdmCreateOfficeFormKeys.postalCode,
  });

  const getCountryIsoCode = () => {
    return isEdit ? phoneISOCode : loggedInUser?.countryCode;
  };

  const userSelectedLanguage: string = useSelector(
    userPreferredLanguageSelector,
  );

  const {
    refetchWithParams: getTimeZoneList,
    data: timeZoneListData,
    isLoading: isLoadingTimeZoneList,
    isError: isErrorTimeZoneList,
  } = useGetTimeZoneList();

  const { data: businessUnitsData, refetchWithParams: refetchBusinessUnits } =
    useGetBusinessUnitsByCountryQuery();

  useEffect(() => {
    getTimeZoneList(userSelectedLanguage);
  }, [userSelectedLanguage]);

  useEffect(() => {
    if (isErrorTimeZoneList) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [isErrorTimeZoneList]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isLoadingOfficeData: {
          isVisible: isLoadingTimeZoneList,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ isLoadingOfficeData: { isVisible: false } }));
    };
  }, [isLoadingTimeZoneList]);

  useEffect(() => {
    const { postalCode } = getValues();
    if (isEdit && !isDefaultPostalCodeSet && postalCode) {
      postalCodeRef.current?.setDefaultValue(postalCode ?? "");
      setIsDefaultPostalCodeSet(true);
    }
  }, [isEdit, watchPostalCode]);

  const defaultCountryData: CountryFieldDetails = {
    label: getLabel(postalCodeKey),
    placeHolder: commonLabels.inputPlaceHolder,
    maxLength: MAX_LENGTH_10,
    isMandatory: true,
    regex: ".*",
    regexNotAllowed: "",
    formatErrorMessage: "",
    blurErrorMessage: "",
  };

  const countryId = useWatch({
    control,
    name: MdmCreateOfficeFormKeys.countryId,
  });

  const initialCountryId = useRef<string | undefined>();
  const [isCountryChanged, setIsCountryChanged] = useState(false);

  useEffect(() => {
    if (isEdit && isNotEmpty(initialCountryId.current)) {
      setIsCountryChanged(countryId !== initialCountryId.current);
    }
  }, [countryId, isEdit]);

  useEffect(() => {
    // Store the initial countryId on mount (for edit mode)
    if (isEdit && !isNotEmpty(initialCountryId.current)) {
      initialCountryId.current = countryId;
    }
  }, [isEdit, countryId]);

  useEffect(() => {
    // Reset business unit field when country changes (for both create and edit modes)
    if (!isEdit || (isEdit && isCountryChanged)) {
      resetField(MdmCreateOfficeFormKeys.businessUnitId);
    }
    if (countryId) refetchBusinessUnits(countryId);
  }, [countryId, isCountryChanged]);


  const fieldSpecialCharactersTooltipMessage = commonValidationMessages.fieldSpecialCharactersTooltipMessage;
  const tooltipHeader = commonValidationMessages.allowedSpecialCharactersTitle;

  return (
    <MuiBox className="container-address-details">
      <MuiGrid container>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.address1}
          >
            <MuiTextField
              label={getLabel(address1)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.address1}`}
              placeholder={commonLabels.inputPlaceHolder}
              showCloseIcon
              maxLength={MAX_LENGTH_50}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={fieldSpecialCharactersTooltipMessage}
              tooltipHeaderTitle={tooltipHeader}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.address2}
          >
            <MuiTextField
              label={getLabel(address2)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.address2}`}
              placeholder={commonLabels.inputPlaceHolder}
              showCloseIcon
              maxLength={MAX_LENGTH_50}
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={fieldSpecialCharactersTooltipMessage}
              tooltipHeaderTitle={tooltipHeader}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.address3}
          >
            <MuiTextField
              label={getLabel(address3)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.address3}`}
              placeholder={commonLabels.inputPlaceHolder}
              showCloseIcon
              maxLength={MAX_LENGTH_50}
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={fieldSpecialCharactersTooltipMessage}
              tooltipHeaderTitle={tooltipHeader}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.countryId}
          >
            <MuiSelect
              label={getLabel(addressCountry)}
              options={setApiDataToDropDownOption(countryListData, true)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.countryId}`}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
              placeholder={commonLabels.selectPlaceHolder}
              showSearch
              searchPlaceholder={commonLabels.search}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController control={control} name={MdmCreateOfficeFormKeys.city}>
            <MuiTextField
              label={getLabel(city)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.city}`}
              placeholder={commonLabels.inputPlaceHolder}
              showCloseIcon
              maxLength={MAX_LENGTH_50}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={fieldSpecialCharactersTooltipMessage}
              tooltipHeaderTitle={tooltipHeader}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.state}
          >
            <MuiTextField
              label={getLabel(stateOrProvince)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.state}`}
              placeholder={commonLabels.stateProvincePlaceHolder}
              showCloseIcon
              maxLength={MAX_LENGTH_75}
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={fieldSpecialCharactersTooltipMessage}
              tooltipHeaderTitle={tooltipHeader}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.postalCode}
            ref={postalCodeRef}
          >
            <TextFieldWithValidation
              id={`${pageId}-${MdmCreateOfficeFormKeys.postalCode}`}
              formId={MdmCreateOfficeFormKeys.postalCode}
              isCountryChanged={countryId}
              zeroValidationMessage={
                commonValidationMessages.greaterThanZeroFieldValidator
              }
              requiredFieldMessage={commonValidationMessages.requiredValidation}
              fieldDetails={{
                ...(getVatPostalDetailsFromCountryKey(
                  countryId,
                  countryVatPostalData,
                )?.postalCode || defaultCountryData),
              }}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.businessUnitId}
          >
            <MuiSelect
              label={getLabel(businessUnit)}
              options={setApiDataToDropDownOption(businessUnitsData)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.businessUnitId}`}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
              placeholder={commonLabels.selectPlaceHolder}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={MdmCreateOfficeFormKeys.timeZoneKey}
          >
            <MuiSelect
              label={getLabel(timeZone)}
              options={setApiDataToDropDownOption(timeZoneListData, true)}
              id={`${pageId}-${MdmCreateOfficeFormKeys.timeZoneKey}`}
              isMandatory
              requiredARIALabel={commonARIALabel.required}
              placeholder={commonLabels.selectPlaceHolder}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </MuiBox>
  );
};
